import { produce } from 'immer';
import { getApolloClient } from '@frameio/graphql-client';
import {
  NOTIFICATIONS_LIST_FILTERS,
  type NotificationsListFilters,
} from '../components/NotificationsListFilter/types';
import { PAGE_SIZE } from '../constants';
import { GetNotificationGroups } from '../gql';

type UpdateCachedNotificationGroupReadStatus = {
  accountId: string;
  filter: NotificationsListFilters;
  notificationId: string;
};

export const updateCachedNotificationGroupReadStatus = ({
  accountId,
  filter,
  notificationId,
}: UpdateCachedNotificationGroupReadStatus) => {
  const client = getApolloClient();

  const existingQuery = client.cache.readQuery({
    query: GetNotificationGroups,
    variables: {
      accountId,
      page: {
        first: PAGE_SIZE,
      },
    },
  });

  const newData = produce(existingQuery, (draft = {}) => {
    if (!draft || !draft.account) return;

    const updatedNotifications = draft.account.notificationGroups.nodes.map(
      (notification) => {
        if (notification.id === notificationId) {
          return {
            ...notification,
            isRead: !notification.isRead,
          };
        }
        return notification;
      }
    );

    draft.account.notificationGroups.nodes = updatedNotifications;

    if (filter === NOTIFICATIONS_LIST_FILTERS.UNREAD) {
      draft.account.notificationGroups.nodes =
        draft.account.notificationGroups.nodes.filter(
          (notification) => !notification.isRead
        );
    }

    const hasUnreads = updatedNotifications.some(
      (notification) => !notification.isRead
    );

    draft.account.notificationGroups.hasUnreads = hasUnreads;
  });

  /** Using `writeQuery` to update the Apollo cache with the updated read/unread
   * notifications. We don't use `cache.modify` as we can't target the
   * Notification fragment due to the various possible typename values.
   */
  return client.cache.writeQuery({
    query: GetNotificationGroups,
    variables: {
      accountId,
      page: {
        first: PAGE_SIZE,
      },
    },
    data: newData,
  });
};
